import { Group } from './AssetGroupsSlice';

// Constants for localStorage persistence
const STORAGE_KEY = 'assetGroups_tempChanges';
const STORAGE_EXPIRY_MINUTES = 5;

export interface PersistedGroupData {
  groups: Group[];
  timestamp: number;
}

/**
 * Saves asset groups data to localStorage with timestamp
 * @param data - The groups data to persist
 */
export const saveGroupsToLocalStorage = (data: PersistedGroupData): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save asset groups to localStorage:', error);
  }
};

/**
 * Loads asset groups data from localStorage if it's still valid (not expired)
 * @returns The persisted data or null if not found/expired/invalid
 */
export const loadGroupsFromLocalStorage = (): PersistedGroupData | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);

    // Validate the structure of the parsed data
    if (!data || typeof data.timestamp !== 'number' || !Array.isArray(data.groups)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    const now = Date.now();
    const timestamp = data.timestamp as number; // Explicit type assertion since we validated above
    const expiryTime = timestamp + STORAGE_EXPIRY_MINUTES * 60 * 1000;

    if (now > expiryTime) {
      // Data has expired, remove it
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return data as PersistedGroupData;
  } catch (error) {
    console.warn('Failed to load asset groups from localStorage:', error);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

/**
 * Clears the persisted asset groups data from localStorage
 */
export const clearGroupsFromLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear asset groups localStorage:', error);
  }
};

/**
 * Checks if there is valid persisted data available
 * @returns true if valid data exists, false otherwise
 */
export const hasValidPersistedData = (): boolean => {
  return loadGroupsFromLocalStorage() !== null;
};

/**
 * Gets the remaining time (in minutes) before the persisted data expires
 * @returns minutes remaining or 0 if no valid data
 */
export const getTimeUntilExpiry = (): number => {
  const data = loadGroupsFromLocalStorage();
  if (!data) return 0;

  const now = Date.now();
  const expiryTime = data.timestamp + STORAGE_EXPIRY_MINUTES * 60 * 1000;
  const remainingMs = Math.max(0, expiryTime - now);

  return Math.floor(remainingMs / (60 * 1000));
};
